import { useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  InboundMailInput,
  InboundMailOutcome,
} from "@shared-core/data/journey-testing-types";
import { Button } from "../../app/components/primitives/button";
import { Input } from "../../app/components/primitives/input";
import { Label } from "../../app/components/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../app/components/primitives/select";
import { describeError } from "./consoleState";

type Fixture = InboundMailInput["attachments"][number]["fixture"];

/**
 * Inbound mail (07 §1): a customer emails their artwork in. Comms ingest records it and artwork intake's handler
 * receives it, placed by the order number in the subject — the LLM triage a real email goes through is not run.
 */
export function InboundMailTab({
  suggestedOrderNo,
}: {
  suggestedOrderNo?: string;
}) {
  const adapter = useDataAdapter();
  const [from, setFrom] = useState("buyer@scenario.example");
  const [subject, setSubject] = useState(
    `Artwork for ${suggestedOrderNo ?? "ORD-…"}`,
  );
  const [fixture, setFixture] = useState<NonNullable<Fixture>>("print_ready");
  const [fileName, setFileName] = useState("business-card.pdf");
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<InboundMailOutcome | null>(null);

  async function send() {
    setSending(true);
    try {
      setOutcome(
        await adapter.sendInboundArtworkMail({
          from_address: from,
          subject,
          attachments: [{ fixture, file_name: fileName }],
        }),
      );
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-4">
      <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
        Triage is not exercised: the email goes straight to the artwork handler.
      </p>
      <div className="grid gap-1.5">
        <Label htmlFor="inbound-from">From</Label>
        <Input
          id="inbound-from"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="inbound-subject">Subject</Label>
        <Input
          id="inbound-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="grid gap-1.5">
          <Label htmlFor="inbound-fixture">Attachment</Label>
          <Select
            value={fixture}
            onValueChange={(v) => setFixture(v as NonNullable<Fixture>)}
          >
            <SelectTrigger id="inbound-fixture">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="print_ready">
                Print-ready business card
              </SelectItem>
              <SelectItem value="cant_print">
                Business card that cannot print
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="inbound-file-name">File name</Label>
          <Input
            id="inbound-file-name"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
          />
        </div>
      </div>
      <Button
        onClick={() => void send()}
        disabled={sending || !from || !subject || !fileName}
      >
        <Send className="size-4" />
        Send
      </Button>
      {outcome && (
        <p className="text-sm" role="status">
          Received {outcome.received}: {outcome.placed} placed on an order,{" "}
          {outcome.orphaned} left for the artwork team
          {outcome.skipped ? `, ${outcome.skipped} skipped` : ""}.
        </p>
      )}
    </div>
  );
}
